import React, { useMemo, useState } from 'react';
import { useListPagedSigningAppointmentsSearchQuery } from '../../../../API/order/signingAppointment/SigningAppointmentAPI';
import { createDefaultPageData } from '../../../../types/rest/IPageData';
import { ErrorBoundaryConstellation } from 'constellationExtended/ErrorBoundary/ErrorBoundaryConstellation';
import { Card } from '@zillow/constellation';
import { TableCardHeader } from '../components/TableCardHeader';
import { SigningAppointmentsTabNames, SigningAppointmentsTabsList } from './SigningAppointmentsTabsList';
import { SigningAppointmentsTable } from './SigningAppointmentsTable';
import { ISigningAppointmentDashboardResponse } from 'types/signingAppointment/ISigningAppointmentDashboardResponse';
import { IOrderParams } from 'API/pcDashboard/getOrderAsync';
import { useDetermineStartDateEndDateParams } from '../hooks/useDetermineStartDateEndDateParams';
import { useManageSigningAppointmentsPagination } from './hooks/useManageSigningAppointmentsPagination';

interface DashboardSigningAppointmentsContainerProps {
  queryWithoutPagination: Omit<IOrderParams, 'pageNumber' | 'pageSize' | 'sortBy'>;
}

const SIGNING_APPOINTMENTS_ENTITY = 'signing-appointments';

export const DashboardSigningAppointmentsContainer: React.FC<DashboardSigningAppointmentsContainerProps> = ({
  queryWithoutPagination,
}) => {
  const [selectedTab, setSelectedTab] = useState(SigningAppointmentsTabNames.AllSigningAppointments);
  const {
    paginationState: [paginationSortParams, setPaginationSortParams],
    revertToFirstPage,
  } = useManageSigningAppointmentsPagination(queryWithoutPagination);
  const determineStartDateEndDateParams = useDetermineStartDateEndDateParams();
  const startEndParams = determineStartDateEndDateParams(selectedTab, SIGNING_APPOINTMENTS_ENTITY);

  const filterParams = useMemo(
    () => ({
      ...queryWithoutPagination,
      ...startEndParams,
      ...paginationSortParams,
    }),
    [queryWithoutPagination, startEndParams, paginationSortParams]
  );

  const {
    data = createDefaultPageData<ISigningAppointmentDashboardResponse>(),
    isFetching,
    refetch,
    isSuccess,
  } = useListPagedSigningAppointmentsSearchQuery(filterParams);

  return (
    <ErrorBoundaryConstellation>
      <Card cardType={'secondary'} cardElevation={0}>
        <TableCardHeader refetch={refetch} title={'Signing Appointments'} />
        <SigningAppointmentsTabsList
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          filterParams={filterParams}
          revertToFirstPage={revertToFirstPage}
        />
        <SigningAppointmentsTable
          data={data}
          isFetching={isFetching}
          isSuccess={isSuccess}
          setSigningAppointmentPaginationSortParams={setPaginationSortParams}
        />
      </Card>
    </ErrorBoundaryConstellation>
  );
};
